import { useRef, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import assetPath from "../../utils/assetPath";

const imgColorPickerIcon = assetPath("/icons/colour-picker-icon.png");

const PRESET_COLORS = ["#94a3b8", "#facc15", "#059669", "#011b21", "#91d2cb", "#ffb688"];

const loyaltySchema = z.object({
  name: z.string().min(1, "Please enter a loyalty tier name"),
  min_points: z
    .number({ error: "Please enter the minimum points" })
    .int("Must be a whole number of 0 or more")
    .min(0, "Must be a whole number of 0 or more"),
  color: z.string().min(1),
});

type LoyaltySchema = z.infer<typeof loyaltySchema>;

export interface LoyaltyFormValues {
  name: string;
  min_points: number;
  color: string;
}

interface LoyaltyFormProps {
  initialValues?: Partial<LoyaltyFormValues>;
  onSave: (values: LoyaltyFormValues) => void;
  onReset?: () => void;
  isPending?: boolean;
  disableMinPoints?: boolean;
}

const LoyaltyForm = ({ initialValues, onSave, onReset, isPending, disableMinPoints }: LoyaltyFormProps) => {
  const [selectedColor, setSelectedColor] = useState(initialValues?.color ?? PRESET_COLORS[0]);
  const colorInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<LoyaltySchema>({
    resolver: zodResolver(loyaltySchema),
    defaultValues: {
      name: initialValues?.name ?? "",
      min_points: initialValues?.min_points,
      color: initialValues?.color ?? PRESET_COLORS[0],
    },
  });

  useEffect(() => {
    setValue("color", selectedColor);
  }, [selectedColor, setValue]);

  const onSubmit = (data: LoyaltySchema) => {
    onSave({ name: data.name, min_points: data.min_points, color: selectedColor });
  };

  const handleReset = () => {
    const color = initialValues?.color ?? PRESET_COLORS[0];
    setSelectedColor(color);
    reset({
      name: initialValues?.name ?? "",
      min_points: initialValues?.min_points,
      color,
    });
    onReset?.();
  };

  const inputClass =
    "w-full rounded-[6px] px-4 py-[13px] text-[14px] font-normal outline-none placeholder:text-[#94a3b8] bg-surface-input text-content-body";
  const labelClass = "text-[12px] font-medium text-content-label";

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="bg-white rounded-[8px] px-[33px] pt-[33px] pb-[49px] flex flex-col gap-6"
      style={{
        border: "1px solid rgba(194,199,201,0.1)",
        boxShadow: "0px 20px 25px -5px rgba(0,0,0,0.1), 0px 8px 10px -6px rgba(0,0,0,0.1)",
      }}
    >
      {/* Loyalty Name */}
      <div className="flex flex-col gap-2">
        <label className={labelClass}>Loyalty Name</label>
        <input
          type="text"
          {...register("name")}
          placeholder="e.g. Diamond Executive"
          className={inputClass}
        />
        {errors.name && <span className="text-[11px] text-content-error">{errors.name.message}</span>}
      </div>

      {/* Min Points */}
      <div className="flex flex-col gap-2">
        <label className={labelClass}>Min Points</label>
        <input
          type="number"
          {...register("min_points", { valueAsNumber: true })}
          placeholder="0"
          min={0}
          disabled={disableMinPoints}
          className={`${inputClass} disabled:opacity-50 disabled:cursor-not-allowed`}
        />
        {errors.min_points && <span className="text-[11px] text-content-error">{errors.min_points.message}</span>}
      </div>

      {/* Tier Visual Identity */}
      <div className="flex flex-col gap-2">
        <label className={labelClass}>Tier Visual Identity</label>
        <div className="flex flex-wrap gap-3">
          {PRESET_COLORS.map((color) => {
            const isSelected = selectedColor === color;
            return (
              <button
                key={color}
                type="button"
                onClick={() => setSelectedColor(color)}
                className="w-8 h-8 rounded-[12px] transition-shadow"
                style={{
                  backgroundColor: color,
                  boxShadow: isSelected
                    ? `0 0 0 2px #fff, 0 0 0 4px ${color}`
                    : "0 0 0 2px #fff",
                }}
                aria-label={`Select color ${color}`}
              />
            );
          })}
          {(() => {
            const isCustom = !PRESET_COLORS.includes(selectedColor);
            return (
              <button
                type="button"
                onClick={() => colorInputRef.current?.click()}
                className="w-8 h-8 rounded-[12px] flex items-center justify-center transition-shadow"
                title="Custom colour"
                style={isCustom ? {
                  backgroundColor: selectedColor,
                  boxShadow: `0 0 0 2px #fff, 0 0 0 4px ${selectedColor}`,
                } : {
                  border: "1px solid #c2c7c9",
                }}
              >
                <img src={imgColorPickerIcon} alt="Custom colour" className="w-[9px] h-[9px]" />
              </button>
            );
          })()}
          <input
            ref={colorInputRef}
            type="color"
            className="sr-only"
            value={selectedColor}
            onChange={(e) => setSelectedColor(e.target.value)}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-3 pt-4">
        <button
          type="submit"
          disabled={isPending}
          className="w-full h-[45px] rounded-[20px] text-white text-[16px] font-bold tracking-[0.64px] transition-opacity hover:opacity-90 disabled:opacity-60 bg-brand-accent"
        >
          {isPending ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              Saving…
            </span>
          ) : "Save"}
        </button>
        <button
          type="button"
          onClick={handleReset}
          className="w-full py-3 rounded-[6px] text-[14px] font-semibold text-center transition-opacity hover:opacity-70 text-brand-teal"
        >
          Reset Changes
        </button>
      </div>
    </form>
  );
};

export default LoyaltyForm;
